import { useMemo, useState } from 'react';
import { List, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { lookupListError } from '@/lib/user-char-lookups';
import type { UserCharLookupOption } from '@/types';

interface Props {
  fieldIndex: number;
  fieldLabel: string;
  options: UserCharLookupOption[];
  onClose: () => void;
  onSave: (options: UserCharLookupOption[]) => void;
}

export function UserCharOptionsDialog({ fieldIndex, fieldLabel, options, onClose, onSave }: Props) {
  const blankRow = (): UserCharLookupOption => ({ fieldIndex, value: '', sortOrder: 0, isDefault: false });
  const [rows, setRows] = useState(() => {
    const initial = options.map(option => ({ ...option, key: crypto.randomUUID() }));
    return initial.some(o => !o.isDeleted) ? initial : [...initial, { ...blankRow(), key: crypto.randomUUID() }];
  });
  const [baseline] = useState(() => JSON.stringify(rows));
  const error = useMemo(() => lookupListError(rows), [rows]);
  const dirty = JSON.stringify(rows) !== baseline;
  const activeCount = rows.filter(r => !r.isDeleted).length;
  const update = (key: string, change: Partial<UserCharLookupOption>) =>
    setRows(previous => previous.map(r => r.key === key ? { ...r, ...change } : r));

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><List className="h-4 w-4 text-primary" />{fieldLabel} lookup values</DialogTitle>
          <DialogDescription>Configure this field’s choices. Save applies to the draft; Save Booking Type persists them.</DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-auto rounded-md border border-border">
          <Table>
            <TableHeader><TableRow><TableHead>Lookup values</TableHead><TableHead className="w-20 text-center">Default</TableHead><TableHead className="w-20 text-center">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.key} className={row.isDeleted ? 'bg-muted/40' : undefined}>
                  <TableCell className="p-2">
                    <Input aria-label={`Lookup value ${index + 1}`} value={row.value} maxLength={255}
                      disabled={row.isDeleted || row.hasEverUsed}
                      onChange={event => update(row.key, { value: event.target.value })} />
                    {(row.isDeleted || row.hasEverUsed) && (
                      <p className="mt-1 text-xs text-muted-foreground">{row.isDeleted ? 'Removed — retained for existing bookings' : 'Used — remove and add a replacement to rename'}</p>
                    )}
                  </TableCell>
                  <TableCell className="p-2 text-center">
                    <Checkbox aria-label={`Default for lookup value ${index + 1}`} checked={row.isDefault} disabled={row.isDeleted}
                      onCheckedChange={checked => setRows(previous => previous.map(r =>
                        ({ ...r, isDefault: r.key === row.key ? checked === true : false })))} />
                  </TableCell>
                  <TableCell className="p-2 text-center">
                    <Button type="button" variant="ghost" size="icon" aria-label={`Delete lookup value ${index + 1}`}
                      title={activeCount <= 1 ? 'At least one active row is required' : 'Delete lookup value'}
                      disabled={row.isDeleted || activeCount <= 1}
                      onClick={() => setRows(previous => row.id && row.hasEverUsed
                        ? previous.map(r => r.key === row.key ? { ...r, isDeleted: true, isDefault: false } : r)
                        : previous.filter(r => r.key !== row.key))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Button type="button" variant="outline" className="w-fit gap-2"
          onClick={() => setRows(previous => [...previous, { ...blankRow(), sortOrder: previous.length, key: crypto.randomUUID() }])}>
          <Plus className="h-4 w-4" />Add row
        </Button>
        {dirty && error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" disabled={!dirty || !!error} onClick={() =>
            onSave(rows.map(({ key: _key, ...row }, index) => ({ ...row, value: row.value.trim(), sortOrder: index })))}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
